/**
 * Anonymous-viewable, read-only summary page reachable from a share link.
 * Route: /s/:token
 *
 * Fetches the get-shared-summary edge function with the token, renders a
 * branded read-only view: docket, topics, stakeholders, executive summary,
 * per-topic sections (collapsed by default), and a Download .docx button.
 * No Generate/Refine controls; no auth required.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Download,
  Sparkles,
  Loader2,
  AlertCircle,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  FileSearch,
} from 'lucide-react';
import { Logo } from '../components/common/Logo';
import { ExecMarkdown } from '../utils/execMarkdown';
import { formatFilingTypeLabel } from '../utils/formatFilingType';
import { useAuth } from '../contexts/AuthContext';
import CitedTopicsViewer from '../components/proceedings/summaries/CitedTopicsViewer';
import { mapFullSummaryToTopics, segmentsForSummary } from '../lib/commentCitations';

interface TopicSection {
  topic_name: string;
  slug?: string;
  source?: 'slim' | 'refined';
  length?: 'brief' | 'detailed' | null;
  markdown: string;
}

interface SharedSummaryMeta {
  id: string;
  docket_number: string;
  topics: string[];
  summary_type: string;
  filing_type: string | null;
  period_type: string | null;
  document_count: number | null;
  topics_count: number | null;
  created_at: string;
  topic_sections?: TopicSection[] | null;
}

interface SharedSummaryResponse {
  summary: SharedSummaryMeta;
  fullSummaryUrl: string | null;
  execUrl: string | null;
  docxUrl: string | null;
}

interface PageAttrJson {
  primary_page?: number;
  page_ranges?: number[];
  disparate_pages?: number[];
  unit?: string;
}

interface PointJson {
  point?: string;
  source_text?: string;
  page_attribution?: PageAttrJson;
}

interface EntityJson {
  entity?: string;
  individual_summary?: PointJson[];
}

interface TopicJson {
  topic?: string;
  summaries_by_topic?: EntityJson[];
}

interface FullSummaryJson {
  summaries?: TopicJson[];
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

/**
 * Strip a leading "Executive Summary" heading from the exec markdown,
 * mirrors the same defense in the docx renderer. The Collapsible
 * already labels the section "Executive summary", so a duplicate
 * heading inside the body is noise.
 */
function stripLeadingExecHeading(md: string): string {
  if (!md) return md;
  const lines = md.split('\n');
  const pat = /^\s*(\*\*Executive\s+Summary\*\*|#{1,3}\s+Executive\s+Summary)\s*$/i;
  if (lines[0] && pat.test(lines[0])) {
    lines.shift();
    if (lines[0] !== undefined && !lines[0].trim()) lines.shift();
  }
  return lines.join('\n');
}

function formatCitation(attr?: PageAttrJson): string | null {
  if (!attr) return null;
  const primary = attr.primary_page;
  const primaryNum =
    typeof primary === 'number' && Number.isFinite(primary) ? primary : null;
  const isPara = attr.unit === 'paragraph';

  // Union primary + page_ranges, dedup, sort ascending. Multi-page
  // positions (introduced on one page, elaborated on another) get
  // rendered "Pages 3, 4 (primarily 4)" so attorneys land on the right
  // page first.
  const unionSet = new Set<number>();
  if (Array.isArray(attr.page_ranges)) {
    for (const n of attr.page_ranges) {
      if (typeof n === 'number' && Number.isFinite(n)) unionSet.add(n);
    }
  }
  if (primaryNum !== null) unionSet.add(primaryNum);
  const union = Array.from(unionSet).sort((a, b) => a - b);

  if (union.length === 0) return null;
  if (union.length === 1) {
    const p = union[0];
    return isPara ? `¶ ${p} (Word doc)` : `Page ${p}`;
  }
  const listed = union.join(', ');
  if (primaryNum !== null && union.length > 1) {
    return isPara
      ? `¶¶ ${listed} (primarily ¶ ${primaryNum}; Word doc)`
      : `Pages ${listed} (primarily ${primaryNum})`;
  }
  return isPara ? `¶¶ ${listed} (Word doc)` : `Pages ${listed}`;
}

/** Collapsible section: chevron header that toggles content visibility. */
const Collapsible: React.FC<{
  /** Stable id so the collapse-all toggle can drive open state externally. */
  id: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Externally-controlled open state. */
  open: boolean;
  onToggle: (id: string) => void;
  children: React.ReactNode;
  className?: string;
}> = ({ id, title, subtitle, open, onToggle, children, className }) => (
  <section
    className={`rounded-lg border border-gray-200 bg-white ${className ?? ''}`}
  >
    <button
      type="button"
      onClick={() => onToggle(id)}
      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
    >
      <div className="min-w-0">
        <h2 className="truncate text-base font-semibold text-gray-900">{title}</h2>
        {subtitle && (
          <p className="mt-0.5 truncate text-xs text-gray-500">{subtitle}</p>
        )}
      </div>
      {open ? (
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
      ) : (
        <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" />
      )}
    </button>
    {open && (
      <div className="border-t border-gray-200 px-5 py-4">{children}</div>
    )}
  </section>
);

export const SharedSummaryPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { openAuthModal } = useAuth();
  const handleTryFree = () =>
    openAuthModal({
      headline: 'Sign in to CommsGenie',
      subhead:
        "Enter your email — we'll send you a magic link. Same flow whether you're new or returning.",
      emailRedirectTo:
        typeof window !== 'undefined'
          ? `${window.location.origin}/onboarding`
          : undefined,
    });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<SharedSummaryMeta | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Open the sourced view automatically when arriving on a deep link.
  const [showSources, setShowSources] = useState(() => searchParams.has('cite'));
  const sourceSegments = useMemo(
    () => (meta ? segmentsForSummary(meta.period_type, meta.filing_type) : undefined),
    [meta?.period_type, meta?.filing_type],
  );

  // Keep the ?cite= param in sync with the active citation so any citation
  // is a shareable deep link.
  const handleActiveChange = useCallback(
    (citation: { id: string } | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (citation) next.set('cite', citation.id);
          else next.delete('cite');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const toggleSources = useCallback(() => {
    setShowSources((v) => {
      const next = !v;
      if (!next) {
        // Leaving the sourced view clears the deep-link param.
        setSearchParams(
          (prev) => {
            const n = new URLSearchParams(prev);
            n.delete('cite');
            return n;
          },
          { replace: true },
        );
      }
      return next;
    });
  }, [setSearchParams]);
  const [exec, setExec] = useState<string | null>(null);
  const [full, setFull] = useState<FullSummaryJson | null>(null);
  const [docxUrl, setDocxUrl] = useState<string | null>(null);

  // Collapsible-section state. All sections default to open so the
  // share recipient sees content immediately; the toolbar toggle
  // (Collapse all / Expand all) is here for long summaries.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleSection = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const isOpen = (id: string) => !collapsed.has(id);

  useEffect(() => {
    if (!token) {
      setError('Missing share token.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/get-shared-summary?token=${encodeURIComponent(token)}`,
          {
            headers: {
              Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            },
          }
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.error || `Server returned ${res.status}`);
        }
        const data: SharedSummaryResponse = await res.json();
        if (cancelled) return;
        setMeta(data.summary);
        setDocxUrl(data.docxUrl);

        // Fetch the rendered exec and the full summary in parallel.
        const [execRes, fullRes] = await Promise.all([
          data.execUrl ? fetch(data.execUrl) : Promise.resolve(null),
          data.fullSummaryUrl ? fetch(data.fullSummaryUrl) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (execRes?.ok) setExec(await execRes.text());
        if (fullRes?.ok) {
          const j = (await fullRes.json()) as FullSummaryJson;
          setFull(j);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load summary');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const filers = useMemo(() => {
    if (!full?.summaries) return [];
    const set = new Set<string>();
    for (const t of full.summaries) {
      for (const e of t.summaries_by_topic ?? []) {
        if (e.entity) set.add(e.entity);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [full]);

  const filingTypeLabel = meta
    ? formatFilingTypeLabel(meta.filing_type, meta.period_type)
    : null;

  return (
    <div className="min-h-screen bg-cg-cream bg-[var(--cg-cream)]" style={{ backgroundColor: 'var(--cg-cream)' }}>
      {/* Slim public header */}
      <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-cg-nav bg-[var(--cg-nav)] px-4 py-3 sm:px-9" style={{ backgroundColor: 'var(--cg-nav)' }}>
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <Link to="/">
            <Logo size="sm" surface="dark" className="hover:scale-[1.02] transition-transform [&_span:last-child]:text-[15px]" />
          </Link>
          <button
            type="button"
            onClick={handleTryFree}
            className="hidden items-center gap-1 rounded-md bg-white/[0.10] px-3 py-1.5 text-[13px] text-white hover:bg-white/[0.18] sm:inline-flex"
          >
            Try CommsGenie free
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-8 sm:py-10">
        {loading && (
          <div className="flex items-center justify-center py-20 text-gray-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading summary…
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-800">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <p className="font-semibold">Couldn't open this summary</p>
                <p className="mt-1 text-sm">{error}</p>
                <p className="mt-3 text-sm">
                  The link may have been revoked, expired, or copied incorrectly.
                </p>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && meta && (
          <>
            {/* Title block */}
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="mb-2 flex items-center gap-2 text-xs">
                  {filingTypeLabel && (
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 font-medium text-blue-800">
                      {filingTypeLabel}
                    </span>
                  )}
                  <span className="text-gray-500">
                    Generated {new Date(meta.created_at).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                  FCC Docket {meta.docket_number} — Comment Summary
                </h1>
                <p className="mt-2 text-sm text-gray-600">
                  {meta.document_count ?? 0} filings analyzed
                  {filers.length > 0 && ` from ${filers.length} stakeholders`}
                  {meta.topics_count ? ` · ${meta.topics_count} topics` : ''}
                </p>
              </div>
              {docxUrl && (
                <a
                  href={docxUrl}
                  download
                  className="inline-flex items-center gap-2 self-start rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
                >
                  <Download className="h-4 w-4" />
                  Download .docx
                </a>
              )}
            </div>

            {/* Topics covered */}
            {meta.topics?.length > 0 && (
              <div className="mb-8 rounded-lg border border-gray-200 bg-white p-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Topics
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {meta.topics.map((t) => (
                    <span
                      key={t}
                      className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-800"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Sourced view toggle — only when grounded per-point data exists. */}
            {(full?.summaries?.length ?? 0) > 0 && (
              <div className="mb-3 flex justify-end">
                <button
                  onClick={toggleSources}
                  className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                    showSources
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  title="Read points side-by-side with the source documents"
                >
                  <FileSearch className="h-4 w-4" />
                  {showSources ? 'Back to summary' : 'View with sources'}
                </button>
              </div>
            )}

            {showSources && meta && (
              <CitedTopicsViewer
                docket={meta.docket_number}
                topics={mapFullSummaryToTopics(full)}
                segments={sourceSegments}
                initialCitationId={searchParams.get('cite') ?? undefined}
                onActiveChange={handleActiveChange}
              />
            )}

            {/* Build collapsible sections. Each gets a stable id used by
                the Collapse-all / Expand-all toolbar. */}
            {!showSources && (() => {
              type SectionEntry = { id: string; title: React.ReactNode; subtitle?: React.ReactNode; body: React.ReactNode };
              const sections: SectionEntry[] = [];

              if (exec) {
                sections.push({
                  id: 'exec',
                  title: (
                    <span className="inline-flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-cg-green" />
                      Executive summary
                    </span>
                  ),
                  body: <ExecMarkdown markdown={stripLeadingExecHeading(exec)} />,
                });
              }

              const sectionsFromRow = meta.topic_sections ?? [];
              if (Array.isArray(sectionsFromRow) && sectionsFromRow.length > 0) {
                sectionsFromRow.forEach((s, i) => {
                  sections.push({
                    id: `topic-${s.slug || i}`,
                    title: s.topic_name,
                    body: s.markdown ? (
                      <ExecMarkdown markdown={s.markdown} />
                    ) : (
                      <p className="text-sm italic text-gray-500">
                        No content generated for this topic.
                      </p>
                    ),
                  });
                });
              } else if (full?.summaries?.length) {
                full.summaries.forEach((t, ti) => {
                  const entities = t.summaries_by_topic ?? [];
                  if (entities.length === 0) return;
                  sections.push({
                    id: `topic-${ti}`,
                    title: t.topic || 'Untitled topic',
                    subtitle: `${entities.length} stakeholder${entities.length === 1 ? '' : 's'}`,
                    body: (
                      <div className="space-y-3">
                        {entities.map((e, ei) => (
                          <div key={`e-${ti}-${ei}`}>
                            <p className="text-sm font-semibold text-gray-900">
                              {e.entity || 'Unknown entity'}
                            </p>
                            <ul className="mt-1 ml-5 list-disc space-y-1.5">
                              {(e.individual_summary ?? []).map((p, pi) => {
                                const cite = formatCitation(p.page_attribution);
                                return (
                                  <li
                                    key={`p-${ti}-${ei}-${pi}`}
                                    className="text-sm text-gray-800 leading-relaxed"
                                  >
                                    {p.point}
                                    {cite && (
                                      <span className="ml-1 text-xs text-gray-500">
                                        ({cite})
                                      </span>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}
                      </div>
                    ),
                  });
                });
              }

              if (filers.length > 0) {
                sections.push({
                  id: 'filers',
                  title: 'Filers included',
                  subtitle: `${filers.length} stakeholders`,
                  body: (
                    <ul className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm text-gray-800 sm:grid-cols-2 lg:grid-cols-3">
                      {filers.map((f) => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                  ),
                });
              }

              const allIds = sections.map((s) => s.id);
              const anyOpen = allIds.some((id) => !collapsed.has(id));
              const collapseAll = () => setCollapsed(new Set(allIds));
              const expandAll = () => setCollapsed(new Set());

              return (
                <>
                  {/* Toolbar */}
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                      {sections.length} section{sections.length === 1 ? '' : 's'}
                    </p>
                    <button
                      type="button"
                      onClick={anyOpen ? collapseAll : expandAll}
                      className="text-xs font-medium text-gray-600 hover:text-gray-900"
                    >
                      {anyOpen ? 'Collapse all' : 'Expand all'}
                    </button>
                  </div>

                  <div className="space-y-3">
                    {sections.map((s) => (
                      <Collapsible
                        key={s.id}
                        id={s.id}
                        title={s.title}
                        subtitle={s.subtitle}
                        open={isOpen(s.id)}
                        onToggle={toggleSection}
                      >
                        {s.body}
                      </Collapsible>
                    ))}
                  </div>
                </>
              );
            })()}

            {/* CTA footer */}
            <div className="mt-12 rounded-lg border border-cg-green/30 bg-cg-green/[0.05] p-6 text-center">
              <p className="text-sm text-gray-700">
                Want to summarize FCC dockets like this?
              </p>
              <button
                type="button"
                onClick={handleTryFree}
                className="mt-3 inline-flex items-center gap-2 rounded-lg bg-cg-green px-4 py-2 text-sm font-medium text-white hover:opacity-90"
              >
                Try CommsGenie free
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SharedSummaryPage;
